// src/pages/budgeting/towerbudgeting/TowerSiteBudgetingList.jsx
// ✅ UPDATED VERSION - Specific SITAC/CME status and buttons
import React, { useState, useEffect } from 'react';
import { Building2, Search, MapPin, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import TowerBudgetingSITACForm from './TowerBudgetingSITACForm';
import TowerBudgetingCMEForm from './TowerBudgetingCMEForm'; // ✅ NEW
import { getAllTowerSites } from '../../../services/towerSiteService';
import { getBOQsByTowerSiteIdAndType } from '../../../services/towerSiteBOQService';
import { getRAPsByTowerSiteId } from '../../../services/towerSiteRAPService';

const TowerSiteBudgetingList = () => {
  const [view, setView] = useState('list');
  const [selectedTower, setSelectedTower] = useState(null);
  const [selectedBudgetingType, setSelectedBudgetingType] = useState(null); // 'SITAC' or 'CME'
  const [towerSites, setTowerSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [towerStatus, setTowerStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvince, setFilterProvince] = useState('ALL');

  useEffect(() => {
    loadTowerSitesWithBOQ();
  }, []);

  const loadTowerSitesWithBOQ = async () => {
    setIsLoading(true);
    try {
      console.log('📂 Loading tower sites with BOQ/RAP status...');
      
      const allTowers = await getAllTowerSites();
      console.log('✅ Loaded tower sites:', allTowers.length);
      
      // Check BOQ and RAP status for each tower
      const statusChecks = await Promise.all(
        allTowers.map(async (tower) => {
          try {
            // Check SITAC BOQ
            const sitacBOQs = await getBOQsByTowerSiteIdAndType(tower.id, 'SITAC');
            const hasSITACBOQ = sitacBOQs && sitacBOQs.length > 0;
            
            // Check CME BOQ
            const cmeBOQs = await getBOQsByTowerSiteIdAndType(tower.id, 'CME');
            const hasCMEBOQ = cmeBOQs && cmeBOQs.length > 0;
            
            let hasSITACRAP = false;
            let hasCMERAP = false;
            
            // Check RAPs if any BOQ exists
            if (hasSITACBOQ || hasCMEBOQ) {
              const raps = await getRAPsByTowerSiteId(tower.id);
              if (raps && raps.length > 0) {
                hasSITACRAP = raps.some(r => r.rapType === 'SITAC');
                hasCMERAP = raps.some(r => r.rapType === 'CME');
              }
            }
            
            return {
              towerId: tower.id,
              hasSITACBOQ,
              hasCMEBOQ,
              hasSITACRAP,
              hasCMERAP
            };
          } catch (error) {
            console.error(`Error checking status for tower ${tower.id}:`, error);
            return {
              towerId: tower.id,
              hasSITACBOQ: false,
              hasCMEBOQ: false,
              hasSITACRAP: false,
              hasCMERAP: false
            };
          }
        })
      );
      
      // Build status map
      const statusMap = {};
      statusChecks.forEach(status => {
        statusMap[status.towerId] = {
          hasSITACBOQ: status.hasSITACBOQ,
          hasCMEBOQ: status.hasCMEBOQ,
          hasSITACRAP: status.hasSITACRAP,
          hasCMERAP: status.hasCMERAP
        };
      });
      
      setTowerStatus(statusMap);
      
      // Filter towers with at least SITAC BOQ
      const towersWithBOQ = allTowers.filter(tower => 
        statusMap[tower.id]?.hasSITACBOQ
      );
      
      setTowerSites(towersWithBOQ);
      
      console.log('✅ Towers with SITAC BOQ:', towersWithBOQ.length);
      console.log('📊 Status breakdown:', {
        total: allTowers.length,
        withSITACBOQ: towersWithBOQ.length,
        withSITACRAP: towersWithBOQ.filter(t => statusMap[t.id]?.hasSITACRAP).length,
        withCMEBOQ: towersWithBOQ.filter(t => statusMap[t.id]?.hasCMEBOQ).length,
        withCMERAP: towersWithBOQ.filter(t => statusMap[t.id]?.hasCMERAP).length
      });
      
      if (towersWithBOQ.length === 0) {
        toast.error('No tower sites with SITAC BOQ found. Please create BOQ first.');
      }
      
    } catch (error) {
      console.error('❌ Error loading tower sites:', error);
      toast.error('Failed to load tower sites');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    loadTowerSitesWithBOQ();
  };

  const handleOpenBudgetingSITAC = (tower) => {
    console.log('🔧 Opening SITAC budgeting for tower:', tower.id);
    setSelectedTower(tower);
    setSelectedBudgetingType('SITAC');
    setView('budgeting-form');
  };

  const handleOpenBudgetingCME = (tower) => {
    console.log('🔧 Opening CME budgeting for tower:', tower.id);
    setSelectedTower(tower);
    setSelectedBudgetingType('CME');
    setView('budgeting-form');
  };

  const handleBackToList = () => {
    console.log('🔙 Returning to tower list');
    setView('list');
    setSelectedTower(null);
    setSelectedBudgetingType(null);
  };

  const handleBudgetingSaved = (savedRAP) => {
    console.log('✅ Budgeting saved:', savedRAP);
    toast.success(`${selectedBudgetingType} RAP saved successfully!`);
    handleBackToList();
    loadTowerSitesWithBOQ(); // Refresh status
  };

  // Filter and search
  const uniqueProvinces = [...new Set(towerSites.map(t => t.provinsi).filter(Boolean))].sort();
  
  const filteredTowers = towerSites.filter(tower => {
    const matchesSearch = 
      !searchTerm ||
      tower.siteId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tower.siteName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tower.kabupaten?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tower.provinsi?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesProvince = 
      filterProvince === 'ALL' || 
      tower.provinsi === filterProvince;
    
    return matchesSearch && matchesProvince;
  });

  const formatHeight = (height) => {
    return height ? `${height} m` : '-';
  };

  // ============= RENDER FORMS =============
  
  if (view === 'budgeting-form' && selectedTower && selectedBudgetingType) {
    if (selectedBudgetingType === 'SITAC') {
      return (
        <TowerBudgetingSITACForm 
          tower={selectedTower} 
          onBack={handleBackToList} 
          onSave={handleBudgetingSaved} 
        />
      );
    } else if (selectedBudgetingType === 'CME') {
      return (
        <TowerBudgetingCMEForm 
          tower={selectedTower} 
          onBack={handleBackToList} 
          onSave={handleBudgetingSaved} 
        />
      );
    }
  }

  // ============= RENDER LIST =============

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Tower Site Budgeting List</h1>
            <p className="text-gray-600 mt-1">Manage Tower Site Budgeting (RAP)</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Site ID, name, or location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={filterProvince}
              onChange={(e) => setFilterProvince(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[200px]"
            >
              <option value="ALL">All Provinces</option>
              {uniqueProvinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Loading tower sites...</p>
            </div>
          </div>
        ) : filteredTowers.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">No tower sites found</p>
              <p className="text-gray-500 text-sm mt-2">
                {searchTerm || filterProvince !== 'ALL' 
                  ? 'Try adjusting your search or filters' 
                  : 'No tower sites with SITAC BOQ available. Please create BOQ first.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">No</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Site ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Site Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Height</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTowers.map((tower, index) => {
                    const status = towerStatus[tower.id] || {};
                    
                    return (
                      <tr key={tower.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{tower.siteId}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{tower.siteName}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="text-gray-900">{tower.kabupaten || '-'}</div>
                              <div className="text-xs text-gray-500">{tower.provinsi || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                            {tower.typeSiteContract || 'Tower'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatHeight(tower.towerHeight)}</td>
                        
                        {/* ✅ UPDATED STATUS BADGES - More Specific */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex flex-col gap-1 items-center">
                            {/* SITAC Status */}
                            {status.hasSITACBOQ && (
                              <div className="flex gap-1">
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                                  BOQ SITAC ✓
                                </span>
                                {status.hasSITACRAP && (
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
                                    RAP SITAC ✓
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* CME Status */}
                            {status.hasCMEBOQ && (
                              <div className="flex gap-1">
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                                  BOQ CME ✓
                                </span>
                                {status.hasCMERAP && (
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">
                                    RAP CME ✓
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        
                        {/* ✅ UPDATED ACTION BUTTONS - SITAC and CME */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col gap-2 items-center">
                            {/* SITAC Button */}
                            <button
                              onClick={() => handleOpenBudgetingSITAC(tower)}
                              className={`font-medium px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg w-40 ${
                                status.hasSITACRAP
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {status.hasSITACRAP ? 'Edit RAP SITAC' : 'Create RAP SITAC'}
                            </button>
                            
                            {/* ✅ CME Button - Only show if CME BOQ exists */}
                            {status.hasCMEBOQ && (
                              <button
                                onClick={() => handleOpenBudgetingCME(tower)}
                                className={`font-medium px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg w-40 ${
                                  status.hasCMERAP
                                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                }`}
                              >
                                {status.hasCMERAP ? 'Edit RAP CME' : 'Create RAP CME'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer Stats */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filteredTowers.length}</span> of{' '}
                  <span className="font-medium">{towerSites.length}</span> tower sites with SITAC BOQ
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="text-gray-700">
                    SITAC RAP:{' '}
                    <span className="font-semibold text-green-600">
                      {filteredTowers.filter(t => towerStatus[t.id]?.hasSITACRAP).length}
                    </span>
                  </div>
                  <div className="text-gray-700">
                    CME BOQ:{' '}
                    <span className="font-semibold text-indigo-600">
                      {filteredTowers.filter(t => towerStatus[t.id]?.hasCMEBOQ).length}
                    </span>
                  </div>
                  <div className="text-gray-700">
                    CME RAP:{' '}
                    <span className="font-semibold text-emerald-600">
                      {filteredTowers.filter(t => towerStatus[t.id]?.hasCMERAP).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TowerSiteBudgetingList;